import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import type { KundeSuchTreffer } from '@/types/bestellung';

type SearchResponse = { kunden: KundeSuchTreffer[] };

export const KUNDEN_SUCHE_MINDESTLAENGE = 2;

/**
 * Kundensuche mit Entprellung (300 ms passt zu Tipp-Geschwindigkeit).
 *
 * `laeuft` ist bewusst mehr als `isFetching`: solange der entprellte Wert
 * der Eingabe hinterherhinkt, ist die Suche aus Sicht des Tippenden
 * bereits im Gange, auch wenn noch keine Anfrage draussen ist. Ohne diese
 * Unterscheidung stand waehrend der Entprellung „Keine Treffer.“ da —
 * eine Aussage ueber ein Ergebnis, das es noch gar nicht gab (B84).
 */
export function useKundenSearch(rawQuery: string) {
  const q = rawQuery.trim();
  const debounced = useDebouncedValue(q, 300);
  const langGenug = q.length >= KUNDEN_SUCHE_MINDESTLAENGE;

  const query = useQuery({
    queryKey: ['kunden', 'search', debounced],
    queryFn: () =>
      apiFetch<SearchResponse>(`/kunden/search.php?q=${encodeURIComponent(debounced)}`),
    enabled: debounced.length >= KUNDEN_SUCHE_MINDESTLAENGE,
    staleTime: 30_000
  });

  return {
    ...query,
    laeuft: langGenug && (debounced !== q || query.isFetching)
  };
}
